import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, MessageCircle } from "lucide-react";
import PageHead from "../components/PageHead";
import ParticleField from "../components/ParticleField";
import Reveal from "../components/Reveal";
import FeaturedCaseStudy from "../components/CaseStudyStrip";
import { PrimaryButton, GhostButton } from "../components/Buttons";
import { heroBgStyle } from "../lib/heroBg";
import { services, team, portfolioCategories } from "../data/site";
import siteContent from "../siteContent";

export default function Home() {
  return (
    <main className="overflow-hidden" data-testid="home-page">
      <PageHead
        title="Digital Archdeacon Media Ltd | Powering Ideas with Technology and Media"
        description="End-to-end ICT and media solutions — software, websites, video production, content creation, media consulting, and printing for businesses across Africa."
      />

      {/* HERO — single column, two-line headline, CTAs above the fold */}
      <section
        className="relative flex items-center min-h-[100svh] lg:min-h-screen"
        style={heroBgStyle}
        data-testid="hero"
      >
        <ParticleField density={70} />
        <div className="container-x relative z-10 pt-28 pb-16 md:pt-32 md:pb-20 max-w-5xl">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-[11px] sm:text-xs uppercase tracking-[0.3em] text-gold font-medium">
              <Sparkles className="h-3.5 w-3.5" />
              {siteContent.home.hero.label}
            </span>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="h-display mt-6 text-[clamp(2.5rem,6vw,5.25rem)]">
              {siteContent.home.hero.heading}
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-[#CBD5E1] leading-relaxed">
              {siteContent.home.hero.subheading}
            </p>
          </Reveal>
          <Reveal delay={360}>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <PrimaryButton to="/request-quote" testId="hero-cta-quote">
                Get a Free Quote
              </PrimaryButton>
              <GhostButton to="/services" testId="hero-cta-services">
                See Our Services
              </GhostButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Featured case study — printing & publishing tribute work */}
      <FeaturedCaseStudy />

      {/* SERVICES SNAPSHOT — light blue */}
      <section className="section-pad section-light-blue relative" data-testid="services-snapshot">
        <div className="absolute inset-0 bg-grid-dark opacity-60 pointer-events-none" />
        <div className="container-x relative">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-electric font-semibold">What we do</span>
            <h2 className="h-display text-4xl sm:text-5xl md:text-6xl text-[#0A0F2C] mt-3">
              How can we help you?
            </h2>
            <p className="mt-5 max-w-2xl text-[#475569] text-base sm:text-lg">
              Whether you're a startup, enterprise, or media organization — we have the tools to bring your digital vision to life.
            </p>
          </Reveal>

          <div className="mt-12 md:mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s, idx) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.id} delay={idx * 70}>
                  <Link
                    to={`/services/${s.id}`}
                    className="block glass-light p-7 h-full group hover:-translate-y-1 transition-transform"
                    data-testid={`service-card-${s.id}`}
                  >
                    <div className="h-12 w-12 rounded-xl grid place-items-center bg-electric/10 border border-electric/20 text-electric">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold mt-5 text-[#0A0F2C]">{s.title}</h3>
                    <p className="text-[#475569] mt-3 leading-relaxed">{s.short}</p>
                    <span className="inline-flex items-center gap-1.5 text-electric text-sm font-semibold mt-5">
                      Learn more
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PORTFOLIO TEASER */}
      <section className="section-pad relative" data-testid="portfolio-teaser">
        <div className="container-x">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">Portfolio</span>
            <h2 className="h-display text-4xl sm:text-5xl md:text-6xl mt-3">Our work speaks for itself.</h2>
            <p className="mt-5 max-w-2xl text-[#CBD5E1] text-base sm:text-lg">
              We've partnered with clients across industries to deliver impactful digital and media solutions.
            </p>
          </Reveal>

          <div className="mt-12 md:mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {portfolioCategories.map((p, idx) => (
              <Reveal key={p.label} delay={idx * 80}>
                <Link
                  to={`/case-studies/${p.slug}`}
                  className="group relative block h-72 rounded-2xl overflow-hidden border border-white/10 hover:border-electric/60 hover:shadow-[0_0_36px_rgba(30,111,255,0.2)] transition-all"
                  data-testid={`portfolio-tile-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <img
                    src={p.image}
                    alt={p.label}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F2C] via-[#0A0F2C]/55 to-transparent" />
                  <div className="absolute inset-0 p-5 flex flex-col justify-end">
                    <p className="font-heading text-2xl font-bold">{p.label}</p>
                    <p className="text-sm text-[#CBD5E1] mt-1">{p.description}</p>
                    <span
                      data-testid={`portfolio-case-cta-${p.slug}`}
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-electric opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300"
                    >
                      View Case Study
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 flex justify-center">
            <a
              href="https://wa.me/2348147276648"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="portfolio-cta-whatsapp"
              className="glow-btn inline-flex items-center gap-2 rounded-full bg-electric px-7 py-4 text-sm md:text-base font-semibold text-white"
            >
              <MessageCircle className="h-4 w-4" />
              Request Our Full Portfolio
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* TEAM — white */}
      <section className="section-pad section-white relative" data-testid="team-snapshot">
        <div className="container-x">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-electric font-semibold">Our People</span>
            <h2 className="h-display text-4xl sm:text-5xl md:text-6xl text-[#0A0F2C] mt-3">Meet our team.</h2>
            <p className="mt-5 max-w-2xl text-[#475569] text-base sm:text-lg">
              Behind every project is a passionate team of ICT professionals and media creatives dedicated to delivering excellence.
            </p>
          </Reveal>

          <div className="mt-12 md:mt-14 grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
            {team.map((m, idx) => (
              <Reveal key={m.name} delay={idx * 100} className="h-full">
                <article
                  className="group rounded-2xl bg-white border border-[#1E6FFF]/20 p-5 h-full flex flex-col hover:-translate-y-2 hover:border-electric hover:shadow-[0_18px_38px_rgba(30,111,255,0.18)] transition-all"
                  data-testid={`team-card-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <div className="rounded-2xl overflow-hidden aspect-square ring-1 ring-electric/30">
                    <img src={m.photo} alt={m.name} loading="lazy" className="h-full w-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold mt-5 text-[#0A0F2C]">{m.name}</h3>
                  <p className="text-electric text-sm font-semibold tracking-wide">{m.role}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="section-pad" data-testid="cta-banner">
        <div className="container-x">
          <Reveal>
            <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-cta-gradient p-10 sm:p-12 md:p-16">
              <div className="absolute -top-32 -right-20 h-80 w-80 rounded-full bg-electric/40 blur-3xl" />
              <div className="absolute -bottom-32 -left-24 h-80 w-80 rounded-full bg-gold/20 blur-3xl" />
              <div className="relative max-w-3xl">
                <h2 className="h-display text-4xl sm:text-5xl md:text-6xl">
                  Ready to take your brand to the next level?
                </h2>
                <p className="mt-6 text-base sm:text-lg text-[#CBD5E1] max-w-2xl">
                  Let's collaborate on your next project — from concept to completion, Digital Archdeacon Media is your partner.
                </p>
                <div className="mt-9">
                  <PrimaryButton to="/request-quote" testId="cta-banner-quote-btn">
                    Get a Free Quote
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
